import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../redux';
import appRoutes from '../../routing/app-routes';
import {
  InvydioState,
  getSessionStatusThunk,
  resetInvydioSessionState,
  setInvydioSession,
  setInvydioWorkflow,
} from '../../store';

import { env } from '../../env';
import { resetReferralCodeState } from '../../store/features/referral-code/referral-code-slice';
import { useAppNavigation } from '../../utils/navigation.utils';

export const useInvydioSessionPageLogic = (): void => {
  const { appNavigate, queryParams } = useAppNavigation();
  const dispatch = useAppDispatch();
  const { invydioSessionStatus, sessionId, getSessionStatus } = useAppSelector(
    (state) => state.features.invydioSession as InvydioState
  );

  // Fallback for device detection if require fails
  const isWebDesktopOrTablet = useMemo(() => {
    if (env.appModule !== 'web') return false;
    try {
      const rdd = require('react-device-detect');
      return rdd.isDesktop || rdd.isTablet;
    } catch (e) {
      console.warn('useInvydioSessionPageLogic: react-device-detect not available, defaulting to true for web');
      return true;
    }
  }, []);

  useEffect(() => {
    const inSessionId = queryParams.get('sessionId');
    const inWorkflowName = queryParams.get('workflowName');
    if (inSessionId) dispatch(setInvydioSession(inSessionId));
    if (inWorkflowName) dispatch(setInvydioWorkflow(inWorkflowName));
  }, [dispatch, queryParams]);

  const invydioUrl = useMemo(() => {
    const url = env.invydioBaseUrl + '/' + (sessionId || '');
    return url;
  }, [sessionId]);

  useEffect(() => {
    if (sessionId && getSessionStatus === 'idle') {
      dispatch(getSessionStatusThunk(sessionId));
    }
  }, [dispatch, sessionId, getSessionStatus]);

  useEffect(() => {
    if (invydioSessionStatus === 'KO') {
      dispatch(resetReferralCodeState());
      dispatch(resetInvydioSessionState());
      appNavigate(appRoutes.ReferralCode, { replace: true });
    }
  }, [dispatch, invydioSessionStatus, appNavigate, getSessionStatus, isWebDesktopOrTablet]);

  useEffect(() => {
    const canRedirect = invydioSessionStatus === 'OK' &&
      env.appModule === 'web' &&
      invydioUrl &&
      isWebDesktopOrTablet;

    if (canRedirect) {
      window.location.href = invydioUrl;
    }
  }, [invydioUrl, invydioSessionStatus, isWebDesktopOrTablet]);

  return null;
};
